import React, { useState, useEffect, useCallback } from 'react';
import { Button, Input, ConfirmationModal } from '../../components/ui';
import { organizationAPI } from '../../api';
import { useToast } from '../../contexts/ToastContext';

// Attio email access (attio-email-capture-via-mcp, plan section 9.3).
// One Attio user per organization is connected by OAuth. Its emails with
// leads are fetched through the Attio MCP server by the button below, or by
// the cron route.

const POLL_MS = 5000;
const DEFAULT_LOOKBACK_DAYS = 30;

// Each failure text names the next action
const RUN_ERROR_TEXT = {
  reauth_required: 'Attio login expired. Click Reconnect to sign in again.',
  time_limit_reached: 'The fetch stopped at its 12-minute limit. Click Fetch emails now to continue from where it stopped.',
  unexpected_mcp_output: 'Attio returned data in a format we did not expect. Check the backend logs for attio_email_sync.',
  body_cap_reached: 'The fetch stopped after 200 emails. Click Fetch emails now to fetch the rest.',
  search_failed: 'Attio did not answer some searches. Click Fetch emails now to try again.',
  body_fetch_failed: 'Attio did not return some email bodies. Click Fetch emails now to try again.',
  connection_changed: 'Attio email access changed during the fetch. Click Fetch emails now to start again.',
  token_refresh_failed: 'Could not refresh the Attio login. Click Fetch emails now. If it fails again, click Reconnect.',
  internal_error: 'The fetch failed. Check the backend logs for attio_email_sync, then click Fetch emails now.',
};

const ALREADY_RUNNING_TEXT = 'A fetch is already running for this organization. Wait for it to finish.';

// Values of ?attioMcp= set by the OAuth callback
const CONNECT_RESULT = {
  connected: { ok: true, text: 'Attio email access is connected.' },
  wrong_workspace: { ok: false, text: 'That Attio login belongs to a different workspace. Sign in to the workspace set up on this tab.' },
  invalid_state: { ok: false, text: 'The Attio login expired or was replaced. Click Connect Attio email access to try again.' },
  exchange_failed: { ok: false, text: 'Attio refused the login. Click Connect Attio email access to try again.' },
  error: { ok: false, text: 'The Attio login was cancelled or failed. Click Connect Attio email access to try again.' },
};

const RUN_STATUS_CLASSES = {
  ok: 'text-green-600 dark:text-green-400',
  partial: 'text-amber-600 dark:text-amber-400',
  failed: 'text-red-600 dark:text-red-400',
};

const COUNT_LABELS = {
  searched: 'Emails found',
  new_emails: 'New emails',
  rows_created: 'Saved to leads',
  leads_matched: 'Leads matched',
  bodies_fetched: 'Bodies fetched',
};

const errorMessage = (err, fallback) =>
  err?.response?.data?.detail?.error?.message ||
  err?.response?.data?.error?.message ||
  fallback;

const formatTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const AttioEmailAccessCard = ({ orgId }) => {
  const toast = useToast();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [lookbackDays, setLookbackDays] = useState(String(DEFAULT_LOOKBACK_DAYS));
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await organizationAPI.getAttioMcpStatus(orgId);
      setState(data);
      return data;
    } catch (err) {
      toast.error(errorMessage(err, 'Could not load the Attio email access status. Reload the page to try again.'));
      return null;
    } finally {
      setLoading(false);
    }
  }, [orgId, toast]);

  useEffect(() => {
    refresh().then((data) => {
      const days = data?.email_sync?.initial_lookback_days;
      if (days) setLookbackDays(String(days));
    });
  }, [refresh]);

  // Reflect the OAuth callback redirect (?attioMcp=connected|error|...), then clear it
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('attioMcp');
    if (!flag) return;
    const result = CONNECT_RESULT[flag] || CONNECT_RESULT.error;
    if (result.ok) toast.success(result.text);
    else toast.error(result.text);
    params.delete('attioMcp');
    const q = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (q ? `?${q}` : ''));
  }, [toast]);

  const running = Boolean(state?.email_sync?.current_run);

  // Poll the status every 5 seconds while a run is live
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [running, refresh]);

  const handleConnect = async () => {
    setBusy(true);
    try {
      const { authorize_url: url } = await organizationAPI.getAttioMcpConnectUrl(orgId, `/organizations/${orgId}`);
      window.location.href = url;
    } catch (err) {
      toast.error(errorMessage(err, 'Could not start the Attio login. Try again.'));
      setBusy(false);
    }
  };

  const handleSync = async () => {
    setBusy(true);
    try {
      const firstRun = !state?.email_sync?.watermark_sent_at;
      const days = parseInt(lookbackDays, 10);
      await organizationAPI.syncAttioEmails(orgId, firstRun && days > 0 ? days : undefined);
      await refresh();
    } catch (err) {
      if (err?.response?.status === 409) toast.error(ALREADY_RUNNING_TEXT);
      else toast.error(errorMessage(err, 'Could not start the fetch. Try again.'));
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    setConfirmDisconnect(false);
    setBusy(true);
    try {
      await organizationAPI.disconnectAttioMcp(orgId);
      toast.success("Attio email access is disconnected. To revoke it in Attio too, remove the app in Attio's settings.");
      await refresh();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not disconnect. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Loading Attio email access…</p>;
  }

  const connection = state?.connection || {};
  const emailSync = state?.email_sync || {};
  const lastRun = emailSync.last_run;
  const connected = connection.connected && connection.status === 'connected';
  const needsReauth = connection.connected && connection.status === 'reauth_required';
  const firstRun = !emailSync.watermark_sent_at;

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-medium text-gray-900 dark:text-white">Attio email access</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Reads the emails exchanged with leads from Attio, and saves them to each lead. Attachments are not read.
        </p>
      </div>

      {/* Not connected */}
      {!connection.connected && (
        <Button size="sm" onClick={handleConnect} disabled={busy}>
          {busy ? 'Opening Attio…' : 'Connect Attio email access'}
        </Button>
      )}

      {/* Login expired */}
      {needsReauth && (
        <div className="space-y-2">
          <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2">
            <p className="text-xs text-amber-800 dark:text-amber-300">{RUN_ERROR_TEXT.reauth_required}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={handleConnect} disabled={busy}>Reconnect</Button>
            <Button size="sm" variant="secondary" onClick={() => setConfirmDisconnect(true)} disabled={busy}>
              Disconnect
            </Button>
          </div>
        </div>
      )}

      {/* Connected */}
      {connected && (
        <div className="space-y-3">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Emails are read as {connection.connected_email || 'the connected Attio user'}. Only mailboxes shared with this user are included.
          </p>

          {firstRun && !running && (
            <div className="max-w-xs">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                First fetch: days to look back
              </label>
              <Input
                type="number"
                min={1}
                max={365}
                value={lookbackDays}
                onChange={(e) => setLookbackDays(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Used only for the first fetch. Later fetches start where the last one ended.
              </p>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={handleSync} disabled={busy || running}>
              {running ? 'Fetching emails…' : 'Fetch emails now'}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setConfirmDisconnect(true)} disabled={busy || running}>
              Disconnect
            </Button>
            {running && (
              <span className="inline-flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-primary" />
                Fetching emails… started {formatTime(emailSync.current_run?.started_at)}
              </span>
            )}
          </div>
        </div>
      )}

      {/* After a run */}
      {lastRun && !running && (connected || needsReauth) && (
        <div className="text-sm space-y-1">
          <p className="text-gray-700 dark:text-gray-300">
            Last fetch: {formatTime(lastRun.started_at)}{' '}
            <span className={`font-medium ${RUN_STATUS_CLASSES[lastRun.status] || ''}`}>{lastRun.status}</span>
          </p>
          {lastRun.counts && (
            <ul className="text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-4">
              {Object.keys(COUNT_LABELS).map((key) => (
                <li key={key}>{COUNT_LABELS[key]}: {lastRun.counts[key] ?? 0}</li>
              ))}
            </ul>
          )}
          {lastRun.error && (
            <p className={lastRun.status === 'failed' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}>
              {RUN_ERROR_TEXT[lastRun.error] || `${lastRun.error}. Check the backend logs for attio_email_sync.`}
            </p>
          )}
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmDisconnect}
        onClose={() => setConfirmDisconnect(false)}
        onConfirm={handleDisconnect}
        title="Disconnect Attio email access?"
        message="Zamanat stops reading emails from Attio. Emails already saved stay. To revoke access in Attio too, remove the app in Attio's settings."
        confirmText="Disconnect"
      />
    </div>
  );
};

export default AttioEmailAccessCard;
